import { RequestStatus } from 'constants/RequestStatus';

import { Loader, MUI, ThemeMode } from 'ci-esi-web-shared';
import { debounce } from 'debounce';
import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CommonSearch } from 'app/components/common/CommonSearch';
import { shapeFileDownloadClick as trackEvent } from 'common/analytics';
import { translations } from 'locales/i18n';
import { log } from 'services/log';
import { LogEvent } from 'services/LogEvent';
import { LogSource } from 'services/LogSource';
import { colors } from 'styles/theme/colors';
import { downloadFileByUrl } from 'utils/download';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { EShapeFileType, ShapefileRegion, getAllRegionsFileByCountry } from '../constants';
import { selectData, selectError, selectRegion, selectStatus } from '../selectors';
import { landingPageShapefileSaga } from './saga';
import { selectFileUrl, selectFilename } from './selectors';
import { ShapefileItems } from './ShapefileItem';
import { actions, reducer, sliceKey } from './slice';

export interface ShapefileModalProps {
  isOpen: boolean;
  onClose(): void;
  activeTab: EShapeFileType;
  changeActiveTab(activeTab: EShapeFileType): void;
}

export function ShapefileModal({ isOpen, onClose, activeTab, changeActiveTab }: ShapefileModalProps) {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: landingPageShapefileSaga });
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const status = useSelector(selectStatus);
  const isLoading = status === RequestStatus.LOADING;
  const error = useSelector(selectError);
  const shapefiles = useSelector(selectData);
  const region = useSelector(selectRegion);
  const fileUrl = useSelector(selectFileUrl);
  const fileName = useSelector(selectFilename);
  const [searchValue, setSearchValue] = useState('');
  const [filteredShapeFiles, setFilteredShapeFiles] = useState(shapefiles);
  const allRegionFile = getAllRegionsFileByCountry(region);

  const dialogPaperSx = {
    minWidth: 480,
    minHeight: 600,
    maxHeight: 600,
    background: isDarkMode ? colors.grey56 : colors.primaryWhite,
  };

  const dialogHeaderSx = { lineHeight: '48px', textAlign: 'center' as const };
  const dialogSearchSx = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    margin: '0 0 15px 0',
  };
  const dialogMenuSx = {
    display: 'flex',
    width: '100%',
    padding: '0 8px 0 0',
  };
  const dialogMenuItemSx = {
    width: '50%',
    textAlign: 'center' as const,
    lineHeight: '32px',
    pointerEvents: isLoading ? ('none' as const) : ('auto' as const),
    cursor: isLoading ? ('not-allowed' as const) : ('pointer' as const),
  };
  const activeMenuItemSx = {
    border: '1px solid grey',
    borderRadius: '5px 5px 0 0',
    borderBottomColor: 'red',
  };
  const dialogContentSx = { padding: theme.spacing(1) };
  const dialogFooterSx = { padding: theme.spacing(3) };
  const cancelSx = { minWidth: 72 };
  const errorSx = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  const handleSearchChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleFilteredShapeFiles = useCallback(() => {
    setFilteredShapeFiles(
      shapefiles.filter(
        (item) =>
          item.toLowerCase().includes(searchValue.toLowerCase()) &&
          !item.toLowerCase().includes(allRegionFile.toLowerCase()),
      ),
    );
  }, [searchValue, shapefiles, allRegionFile]);

  const debouncedShapeFilesFiltering = useMemo(
    () => debounce(handleFilteredShapeFiles, 700),
    [handleFilteredShapeFiles],
  );

  useEffect(() => {
    if (shapefiles.length > 0 && allRegionFile) {
      const filteredFiles = shapefiles.filter((item) => !item?.toLowerCase().includes(allRegionFile.toLowerCase()));
      setFilteredShapeFiles(filteredFiles);
    }

    return () => {
      setSearchValue('');
    };
  }, [shapefiles, allRegionFile]);

  useEffect(() => {
    debouncedShapeFilesFiltering();
  }, [searchValue, debouncedShapeFilesFiltering]);

  const handleDownload = useCallback(
    (filename: string, downloadFromPlay = false) => {
      trackEvent(region ?? '', fileName);
      dispatch(actions.loadShapefile({ region, filename, type: downloadFromPlay ? EShapeFileType.PLAY : activeTab }));
      onClose();
    },
    [dispatch, fileName, onClose, region, activeTab],
  );

  useEffect(() => {
    if (fileUrl !== null) {
      downloadFileByUrl(fileUrl);
      log.info({
        event: LogEvent.DOWNLOAD,
        source: LogSource.SHAPEFILE_DOWNLOAD,
        fileName: fileName,
        region: region,
      });
      dispatch(actions.resetFilename());
    }
  }, [dispatch, fileName, fileUrl, region]);

  return (
    <MUI.Dialog PaperProps={{ sx: dialogPaperSx }} onClose={onClose} open={isOpen}>
      <MUI.Typography variant="h2" sx={dialogHeaderSx}>
        {`${t(translations.DOWNLOAD_SHAPEFILE.MODAL_TITLE)} - ${t(
          region && region === ShapefileRegion.US
            ? translations.DOWNLOAD_SHAPEFILE.ITEMS.US
            : translations.DOWNLOAD_SHAPEFILE.ITEMS.CANADA,
        )}`}
      </MUI.Typography>
      <MUI.Box sx={dialogSearchSx}>
        <CommonSearch
          onChange={handleSearchChange}
          value={searchValue}
          placeholder={t(translations.COMMON.SEARCH_PLACEHOLDER)}
          shapeFileModal
        />
      </MUI.Box>
      <MUI.Box sx={dialogMenuSx}>
        <MUI.Box
          sx={{ ...dialogMenuItemSx, ...(EShapeFileType.PLAY === activeTab ? activeMenuItemSx : {}) }}
          onClick={() => changeActiveTab(EShapeFileType.PLAY)}
        >
          <span>{t(translations.DOWNLOAD_SHAPEFILE.PLAY_LEVEL_FILES)}</span>
        </MUI.Box>
        <MUI.Box
          sx={{ ...dialogMenuItemSx, ...(EShapeFileType.OPERATOR === activeTab ? activeMenuItemSx : {}) }}
          onClick={() => changeActiveTab(EShapeFileType.OPERATOR)}
        >
          <span>{t(translations.DOWNLOAD_SHAPEFILE.OPERATOR_LEVEL_FILES)}</span>
        </MUI.Box>
      </MUI.Box>

      <MUI.DialogContent sx={dialogContentSx} dividers>
        {status === RequestStatus.LOADING && <Loader />}
        {status === RequestStatus.FAILURE && (error as ReactNode) && (
          <MUI.Box sx={errorSx}>
            <MUI.Typography>{t('DOWNLOAD_SHAPEFILE.ERROR')}</MUI.Typography>
          </MUI.Box>
        )}
        {status === RequestStatus.SUCCESS && (
          <MUI.List>
            {<ShapefileItems items={[allRegionFile]} handleDownload={handleDownload} shouldDownloadFromPlay />}
            {<ShapefileItems items={filteredShapeFiles} handleDownload={handleDownload} />}
          </MUI.List>
        )}
      </MUI.DialogContent>
      <MUI.DialogActions sx={dialogFooterSx}>
        <MUI.Button size="small" variant="contained" color="secondary" sx={cancelSx} autoFocus onClick={onClose}>
          {t('COMMON.CANCEL')}
        </MUI.Button>
      </MUI.DialogActions>
    </MUI.Dialog>
  );
}
